// Deterministic flow controller for supplier chatbot sidebar actions.
// Replaces prompt-driven orchestration with a client-side state machine.

import type { SessionContact } from "./chatClient";

export type FlowType = "message" | "inquiry";

export type FlowStep =
  | "idle"
  | "confirm_email"
  | "collect_email"
  | "collect_message"
  | "collect_description"
  | "collect_phone"
  | "complete";

export interface FlowState {
  type: FlowType | null;
  step: FlowStep;
  email: string | null;
  message: string | null;
  description: string | null;
  phone: string | null;
}

export interface FlowResult {
  nextState: FlowState;
  assistantMessage: string;
  action: FlowAction | null;
  detectedEmail: string | null;
  detectedPhone: string | null;
}

export type FlowAction =
  | { type: "direct_msg"; email: string; message: string }
  | { type: "inquiry"; email: string; phone: string | null; description: string };

export const INITIAL_FLOW_STATE: FlowState = {
  type: null,
  step: "idle",
  email: null,
  message: null,
  description: null,
  phone: null,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
const PHONE_RE = /(\+?\d[\d\s\-]{7,14}\d)/;

const CONFIRM_PATTERNS = /^(ano|jo|jasně|ok|potvrdit|správně|sure|yes|yeah|jj|jojo|a|aha|přesně|souhlasím|jasne|spravne)$/i;
const REUSE_PATTERNS = /(už máš|použij|předchozí|stejný|ten co|máš ho|máš mail|uz mas|pouzij|predchozi|stejny|mas ho|mas mail)/i;
const DECLINE_PATTERNS = /^(ne|nechci|jiný|jiny|nový|novy|no)$/i;
const SKIP_PATTERNS = /^(ne|nechci|přeskočit|preskocit|bez telefonu|nemám|nemam|skip)$/i;

export function validateEmail(input: string): string | null {
  const m = input.match(EMAIL_RE);
  return m ? m[0] : null;
}

function extractPhone(input: string): string | null {
  const m = input.match(PHONE_RE);
  return m ? m[1].trim() : null;
}

export function isFlowActive(state: FlowState): boolean {
  return state.step !== "idle";
}

// ---------------------------------------------------------------------------
// Templates (Czech)
// ---------------------------------------------------------------------------

const T = {
  confirm_email: (email: string) =>
    `Mám použít e-mail **${email}**? (ano / ne)`,
  collect_email: "Na jaký e-mail vám má firma odpovědět?",
  collect_email_invalid:
    "Zadejte prosím platnou e-mailovou adresu (např. jan@email.cz).",
  collect_message: "Co chcete firmě sdělit?",
  collect_description:
    "Popište prosím, co potřebujete – typ práce, rozměry, materiál, termín…",
  collect_phone:
    'Chcete uvést i telefonní číslo pro rychlejší kontakt? (nepovinné – napište "ne" pro přeskočení)',
  complete_message: (company: string, email: string) =>
    `✅ Zpráva odeslána! Firma **${company}** vás bude kontaktovat na **${email}**.`,
  complete_inquiry: (company: string) =>
    `✅ Poptávka odeslána! Firma **${company}** se vám ozve.`,
  confirm_unclear:
    'Chcete použít tento e-mail? Odpovězte **"ano"** nebo **"ne"**.',
  intro_message: "Chcete napsat zprávu firmě. Pojďme na to!",
  intro_inquiry: "Chcete vytvořit poptávku. Pojďme na to!",
};

// ---------------------------------------------------------------------------
// startFlow — called when sidebar button is clicked
// ---------------------------------------------------------------------------

export function startFlow(
  type: FlowType,
  sessionContact: SessionContact,
  companyName: string,
): FlowResult {
  const base: FlowState = {
    ...INITIAL_FLOW_STATE,
    type,
  };

  const intro = type === "message" ? T.intro_message : T.intro_inquiry;

  if (sessionContact.email) {
    return {
      nextState: { ...base, step: "confirm_email", email: sessionContact.email },
      assistantMessage: `${intro}\n\n${T.confirm_email(sessionContact.email)}`,
      action: null,
      detectedEmail: null,
      detectedPhone: null,
    };
  }

  return {
    nextState: { ...base, step: "collect_email" },
    assistantMessage: `${intro}\n\n${T.collect_email}`,
    action: null,
    detectedEmail: null,
    detectedPhone: null,
  };
}

// ---------------------------------------------------------------------------
// advanceFlow — called on each user message while flow is active
// ---------------------------------------------------------------------------

export function advanceFlow(
  state: FlowState,
  userInput: string,
  sessionContact: SessionContact,
  companyName: string,
): FlowResult {
  const input = userInput.trim();

  switch (state.step) {
    case "confirm_email":
      return handleConfirmEmail(state, input, sessionContact, companyName);
    case "collect_email":
      return handleCollectEmail(state, input, companyName);
    case "collect_message":
      return handleCollectMessage(state, input, companyName);
    case "collect_description":
      return handleCollectDescription(state, input, sessionContact, companyName);
    case "collect_phone":
      return handleCollectPhone(state, input, companyName);
    default:
      return {
        nextState: INITIAL_FLOW_STATE,
        assistantMessage: "",
        action: null,
        detectedEmail: null,
        detectedPhone: null,
      };
  }
}

// ---------------------------------------------------------------------------
// Step handlers
// ---------------------------------------------------------------------------

function handleConfirmEmail(
  state: FlowState,
  input: string,
  sessionContact: SessionContact,
  companyName: string,
): FlowResult {
  const confirmed =
    CONFIRM_PATTERNS.test(input) || REUSE_PATTERNS.test(input);

  if (confirmed && state.email) {
    return advanceAfterEmail(state, state.email, companyName, sessionContact);
  }

  if (DECLINE_PATTERNS.test(input)) {
    return {
      nextState: { ...state, step: "collect_email", email: null },
      assistantMessage: T.collect_email,
      action: null,
      detectedEmail: null,
      detectedPhone: null,
    };
  }

  // Maybe user typed a new email directly
  const newEmail = validateEmail(input);
  if (newEmail) {
    return advanceAfterEmail(state, newEmail, companyName, sessionContact);
  }

  return {
    nextState: state,
    assistantMessage: T.confirm_unclear,
    action: null,
    detectedEmail: null,
    detectedPhone: null,
  };
}

function handleCollectEmail(
  state: FlowState,
  input: string,
  companyName: string,
): FlowResult {
  const email = validateEmail(input);
  if (!email) {
    return {
      nextState: state,
      assistantMessage: T.collect_email_invalid,
      action: null,
      detectedEmail: null,
      detectedPhone: null,
    };
  }

  return advanceAfterEmail(state, email, companyName, {});
}

function advanceAfterEmail(
  state: FlowState,
  email: string,
  companyName: string,
  sessionContact: SessionContact,
): FlowResult {
  if (state.type === "message") {
    return {
      nextState: { ...state, step: "collect_message", email },
      assistantMessage: T.collect_message,
      action: null,
      detectedEmail: email,
      detectedPhone: null,
    };
  }

  // inquiry
  return {
    nextState: { ...state, step: "collect_description", email },
    assistantMessage: T.collect_description,
    action: null,
    detectedEmail: email,
    detectedPhone: null,
  };
}

function handleCollectMessage(
  state: FlowState,
  input: string,
  companyName: string,
): FlowResult {
  if (!input) {
    return {
      nextState: state,
      assistantMessage: T.collect_message,
      action: null,
      detectedEmail: null,
      detectedPhone: null,
    };
  }

  const email = state.email!;
  return {
    nextState: { ...state, step: "complete", message: input },
    assistantMessage: T.complete_message(companyName, email),
    action: { type: "direct_msg", email, message: input },
    detectedEmail: null,
    detectedPhone: null,
  };
}

function handleCollectDescription(
  state: FlowState,
  input: string,
  sessionContact: SessionContact,
  companyName: string,
): FlowResult {
  if (!input) {
    return {
      nextState: state,
      assistantMessage: T.collect_description,
      action: null,
      detectedEmail: null,
      detectedPhone: null,
    };
  }

  const nextState = { ...state, description: input };

  // Skip phone if already known
  if (sessionContact.phone) {
    const email = state.email!;
    return {
      nextState: { ...nextState, step: "complete", phone: sessionContact.phone },
      assistantMessage: T.complete_inquiry(companyName),
      action: { type: "inquiry", email, phone: sessionContact.phone, description: input },
      detectedEmail: null,
      detectedPhone: null,
    };
  }

  return {
    nextState: { ...nextState, step: "collect_phone" },
    assistantMessage: T.collect_phone,
    action: null,
    detectedEmail: null,
    detectedPhone: null,
  };
}

function handleCollectPhone(
  state: FlowState,
  input: string,
  companyName: string,
): FlowResult {
  const email = state.email!;
  const description = state.description!;

  if (SKIP_PATTERNS.test(input)) {
    return {
      nextState: { ...state, step: "complete" },
      assistantMessage: T.complete_inquiry(companyName),
      action: { type: "inquiry", email, phone: null, description },
      detectedEmail: null,
      detectedPhone: null,
    };
  }

  const phone = extractPhone(input);
  if (phone) {
    return {
      nextState: { ...state, step: "complete", phone },
      assistantMessage: T.complete_inquiry(companyName),
      action: { type: "inquiry", email, phone, description },
      detectedEmail: null,
      detectedPhone: phone,
    };
  }

  // Treat any non-empty input that looks like it could be a phone attempt
  // but doesn't parse — ask again
  return {
    nextState: state,
    assistantMessage: T.collect_phone,
    action: null,
    detectedEmail: null,
    detectedPhone: null,
  };
}
